import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { max, min } from '@adornis/validation/functions/range.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { Permission } from '../../permissions/roles.js';
import { GroupsAPI } from '../api/GroupsAPI.js';
import { Group } from '../entities/Group.js';
import { Kostenstelle, KostenstelleSubGroup } from '../entities/Kostenstelle.js';
import { School } from '../entities/School.js';

const translateGroupFieldMiddleware = (formField: FunctionalFormFieldProps<string>) => {
  const classMap = {
    'Allgemeine Gruppe': KostenstelleSubGroup._class,
    Kostenstelle: Kostenstelle._class,
    Schule: School._class,
  };

  const value = Object.entries(classMap).find(([_, v]) => v === formField.value)?.[0] ?? formField.value;

  return {
    ...formField,
    value,
    onPicked: value => {
      formField.onPicked?.(value ? classMap[value] : null);
    },
  };
};

const SGGroupCreationDialogContent = stateful(
  (props: {
    prefill?: {
      pickedClass?: string;
      pickedName?: string;
      pickedSuffix?: number;
      pickedSchoolIDs?: string[];
    };
    resolve: (result: {
      pickedClass: string;
      pickedName: string;
      pickedSuffix: number | null;
      pickedSchoolID: string | null;
    }) => void;
    reject: () => void;
  }) => {
    const [obj, setObj] = useState(
      {
        class: props.prefill?.pickedClass ?? null,
        name: props.prefill?.pickedName ?? null,
        suffix: props.prefill?.pickedSuffix ?? null,
        schoolID: props.prefill?.pickedSchoolIDs?.length === 1 ? props.prefill.pickedSchoolIDs[0] : null,
      },
      [],
    );

    const user = useObservable(() => currentUser(), []);

    const form = useForm(obj, {
      validator: {
        class: sgNonOptional(),
        name: sgNonOptional(),
        suffix: async options => {
          if (form.document.class !== Kostenstelle._class) return;
          await and(sgNonOptional({ allowFalsy: true }), max(99), min(0))(options);
          // creating checks this again on the server, here it only keeps the dialog open at the field
          if (
            form.document.schoolID &&
            (await GroupsAPI.checkKostenstelleSuffixTaken(form.document.schoolID, options.value)())
          )
            throw new ValidationError('Dieser Kostenstellensuffix ist an der Schule bereits vergeben.', {
              key: options.key,
            });
        },
        schoolID: options => (form.document.class === Kostenstelle._class ? sgNonOptional()(options) : undefined),
      },
    });

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Gruppe erstellen', close: () => props.reject(), closeLabel: 'Dialog schließen' })}
        ${
          user?.hasPermission(Permission.CREATE_GROUP, ANY_CONTEXT)
            ? DropdownSelectionInput({
                ...translateGroupFieldMiddleware(form.field('class')),
                placeholder: 'Gruppenart',
                selectables: ['Allgemeine Gruppe', 'Kostenstelle', 'Schule'],
              })
            : TextInput({
                value: form.field('class').value,
                disabled: true,
                placeholder: 'Gruppenart',
              })
        }
        ${TextInput({
          ...form.field('name'),
          placeholder: 'Name',
        })}
        ${
          form.document.class === Kostenstelle._class
            ? html`
                ${EntityPickerInput<typeof School>({
                  ...form.field('schoolID'),
                  placeholder: 'Schule',
                  class: School,
                  operation: GroupsAPI.subscribeGroups,
                  filter: new AdornisFilter({
                    searchFields: ['name'],
                    aspects: [
                      new StringAspect({ mode: 'equals', fieldName: '_class', value: School._class }),
                      new BooleanAspect({ customID: 'onlyInternal', value: true }),
                      ...(props.prefill?.pickedSchoolIDs?.length && props.prefill?.pickedSchoolIDs?.length > 1
                        ? [
                            new ListAspect({
                              mode: 'containsOR',
                              fieldName: '_id',
                              values: props.prefill?.pickedSchoolIDs,
                            }),
                          ]
                        : []),
                    ],
                  }),
                  selectionSet: { name: 1, _id: 1, kostenstellePrefix: 1 },
                  renderString: item => `(${item.kostenstellePrefix ?? ''}) ${item.name}`,
                })}
                ${NumberInput({
                  ...form.field('suffix'),
                  placeholder: 'Kostenstellensuffix',
                })}
              `
            : nothing
        }
        <button
          type="button"
          class="btn btn-primary"
          @click=${async () => {
            const errors = await form.validate();
            if (errors) return;

            props.resolve({
              pickedClass: form.document.class,
              pickedName: form.document.name,
              pickedSuffix: form.document.class === Kostenstelle._class ? form.document.suffix : null,
              pickedSchoolID: form.document.schoolID,
            });
          }}
        >
          Erstellen
        </button>
      </div>
    `;
  },
);

export const SGGroupCreationDialog = async (
  skipJumpTo?: boolean,
  prefill?: {
    pickedClass?: string;
    pickedName?: string;
    pickedSuffix?: number;
    pickedSchoolIDs?: string[];
  },
): Promise<string | null> => {
  const { pickedClass, pickedName, pickedSuffix, pickedSchoolID } = await showDialog<{
    pickedClass: string;
    pickedName: string;
    pickedSuffix: number | null;
    pickedSchoolID: string | null;
  }>({
    content: (resolve, reject) => SGGroupCreationDialogContent({ prefill, resolve, reject }),
  });

  skipJumpTo ??= false;

  let newGroup = new Group({
    name: pickedName,
  });
  newGroup._class = pickedClass;
  newGroup.parentIDs = pickedSchoolID ? [pickedSchoolID] : [];

  if (pickedClass === Kostenstelle._class) {
    newGroup = new Kostenstelle({
      ...newGroup.toJSON(),
      kostenstelleSuffix: pickedSuffix,
    });
  }

  let newId: string | null = null;

  await guardDialog<string>(async () => newGroup.create(), {
    success: id => {
      newId = id;
      console.log(newId);
      void XSnackbar.show('Erfolgreich erstellt');

      if (!skipJumpTo) {
        goTo(`/groups/${id}`);
      }
    },
  });

  return newId;
};
